/**
 * Stock quantities (Phase 2 plan §2.1, D226). Every item has one stock unit, and a quantity is a
 * whole number of that unit — never a decimal, in the database, the API or on a device:
 *
 * - `G`  gram        — weights (shown as kg: 1 kg = 1,000 g)
 * - `ML` millilitre  — liquids (shown as L: 1 L = 1,000 ml)
 * - `PC` piece       — counted things (shown as pieces)
 *
 * A person types "12.345" kg; it is read **as text** into 12,345 g, so no floating point is ever
 * involved, and a fourth decimal is refused rather than rounded. Purchase units (a box of 24, a
 * 25 kg sack) are whole multiples of the stock unit (`toBaseQuantity`).
 */
import { latinDigits, stripInvisible } from '../network/address.ts';

export const STOCK_UNITS = ['G', 'ML', 'PC'] as const;
export type StockUnit = (typeof STOCK_UNITS)[number];

/** How a quantity of each stock unit is shown: kg, L or pieces. */
export type DisplayUnit = 'KG' | 'L' | 'PC';

export const DISPLAY_UNIT: Readonly<Record<StockUnit, DisplayUnit>> = {
  G: 'KG',
  ML: 'L',
  PC: 'PC',
};

/** Stock units in one display unit: 1 kg = 1,000 g, 1 L = 1,000 ml, a piece is a piece. */
export const DISPLAY_FACTOR: Readonly<Record<StockUnit, number>> = { G: 1000, ML: 1000, PC: 1 };

/** Decimals a person may type in the display unit: grams and millilitres, never part of a piece. */
const DISPLAY_DECIMALS: Readonly<Record<StockUnit, number>> = { G: 3, ML: 3, PC: 0 };

export type QuantityProblem = 'invalid' | 'too_many_decimals' | 'too_large';

export type QuantityParse =
  | { readonly ok: true; readonly value: number | null }
  | { readonly ok: false; readonly problem: QuantityProblem; readonly maxDecimals: number };

/**
 * Reads a quantity as a person types it, in the display unit (kg, L or pieces) unless `in` says
 * the stock unit itself. Accepts Arabic-Indic digits, the Arabic decimal mark (٫), and thousands
 * separators (`1,250.5`, `1 250`, `١٬٢٥٠`). Empty → `value: null` (nothing typed). Zero is a
 * valid quantity (a count of 0); callers that need more than zero check it themselves.
 */
export function parseQuantity(
  input: string,
  unit: StockUnit,
  options: { readonly in?: 'display' | 'stock' } = {},
): QuantityParse {
  const factor = options.in === 'stock' ? 1 : DISPLAY_FACTOR[unit];
  const maxDecimals = options.in === 'stock' ? 0 : DISPLAY_DECIMALS[unit];
  const text = latinDigits(stripInvisible(input)).replace(/[\s,،٬]/g, '');
  if (text === '') return { ok: true, value: null };
  const match = /^(\d+)(?:\.(\d*))?$/.exec(text);
  if (!match) return { ok: false, problem: 'invalid', maxDecimals };
  const whole = match[1] ?? '';
  const fraction = (match[2] ?? '').replace(/0+$/, '');
  if (fraction.length > maxDecimals)
    return { ok: false, problem: 'too_many_decimals', maxDecimals };
  const value =
    BigInt(whole) * BigInt(factor) +
    (fraction === '' ? 0n : BigInt(fraction.padEnd(maxDecimals, '0')));
  if (value > BigInt(Number.MAX_SAFE_INTEGER)) {
    return { ok: false, problem: 'too_large', maxDecimals };
  }
  return { ok: true, value: Number(value) };
}

function group(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * A quantity in its display unit, as text with Latin digits and thousands separators (O16):
 * 12,345 g → "12.345", 12,500 g → "12.5", 3,000 ml → "3", 1,250 pieces → "1,250". Negative
 * quantities (stock gone below zero offline, plan §14.2) keep their sign. The unit's name comes
 * from the app's translations (`DISPLAY_UNIT`).
 */
export function formatQuantity(stockQuantity: number, unit: StockUnit): string {
  if (!Number.isSafeInteger(stockQuantity)) throw new RangeError('quantity must be a safe integer');
  const factor = DISPLAY_FACTOR[unit];
  const sign = stockQuantity < 0 ? '-' : '';
  const absolute = Math.abs(stockQuantity);
  const whole = Math.trunc(absolute / factor);
  const rest = absolute - whole * factor;
  if (rest === 0) return `${sign}${group(String(whole))}`;
  const decimals = String(rest)
    .padStart(String(factor).length - 1, '0')
    .replace(/0+$/, '');
  return `${sign}${group(String(whole))}.${decimals}`;
}

/** Quantity in a purchase unit (e.g. 10 boxes) × its factor (24 pieces a box) → stock units. */
export function toBaseQuantity(quantityInUnit: number, factor: number): number {
  if (!Number.isSafeInteger(quantityInUnit) || !Number.isSafeInteger(factor) || factor < 1) {
    throw new RangeError('quantity and factor must be safe integers, factor at least 1');
  }
  const value = BigInt(quantityInUnit) * BigInt(factor);
  if (value > BigInt(Number.MAX_SAFE_INTEGER) || value < BigInt(Number.MIN_SAFE_INTEGER)) {
    throw new RangeError('quantity too large');
  }
  return Number(value);
}
